import HomeIcon from '@mui/icons-material/HomeOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import PetsIcon from '@mui/icons-material/PetsOutlined';
import AutoStoriesIcon from '@mui/icons-material/AutoStoriesOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBagOutlined';
import type { HubAudience } from './hubAudience';

// Which mobile surface a destination lives on: the persistent bottom bar, or the "More" sheet.
export type MobileSlot = 'bar' | 'more';

export type NavItem = {
  name: string;
  path: string;
  Icon: typeof HomeIcon;
  title: string;
  // Shorter label used in the space-constrained bottom bar; falls back to `title`.
  barLabel?: string;
  visible: boolean;
  showInDrawer: boolean;
  mobileSlot: MobileSlot;
};

// The destinations a pre-surrender pet parent sees (ticket 2530), in ticket order.
// Everything else (Albums, Community, My Photos — and, per QA, My Pets, which is
// empty for pre-surrender animals) is hidden for them.
const PRE_SURRENDER_NAV = new Set([
  'hub/home', 'hub/messages', 'hub/lessons-and-guides', 'hub/products', 'hub/settings',
]);

// The destinations a foster sees (ticket 2538): the adopter set minus Albums and My Pets.
const FOSTER_NAV = new Set([
  'hub/home', 'hub/messages', 'hub/lessons-and-guides', 'hub/products', 'hub/community', 'hub/photos',
  'hub/settings',
]);

// Single canonical, ordered list of Hub destinations. The desktop sidebar renders all of
// them in this order; the mobile bar renders the `bar` items (plus a "More" entry) and the
// More sheet renders the `more` items.
const getAllNavItems = (membershipExpired: boolean): NavItem[] => [
  {
    name: 'hub/home',
    path: '/hub/home',
    Icon: HomeIcon,
    title: 'Home',
    visible: true,
    showInDrawer: true,
    mobileSlot: 'bar',
  },
  {
    name: 'hub/messages',
    path: '/hub/messages',
    Icon: SmsOutlinedIcon,
    title: 'Messages',
    visible: true,
    showInDrawer: true,
    mobileSlot: 'bar',
  },
  {
    name: 'hub/lessons-and-guides',
    path: '/hub/lessons-and-guides',
    Icon: SchoolIcon,
    title: 'Lessons & Guides',
    barLabel: 'Lessons',
    visible: !membershipExpired,
    showInDrawer: true,
    mobileSlot: 'bar',
  },
  {
    name: 'hub/albums',
    path: '/hub/albums',
    Icon: AutoStoriesIcon,
    title: 'Albums',
    visible: true,
    showInDrawer: true,
    mobileSlot: 'bar',
  },
  {
    name: 'hub/products',
    path: '/hub/products',
    Icon: ShoppingBagIcon,
    title: 'Products',
    visible: true,
    showInDrawer: true,
    mobileSlot: 'more',
  },
  {
    name: 'hub/community',
    path: '/hub/community',
    Icon: PublicOutlinedIcon,
    title: 'Community',
    visible: true,
    showInDrawer: true,
    mobileSlot: 'more',
  },
  {
    name: 'hub/photos',
    path: '/hub/photos',
    Icon: PhotoCameraOutlinedIcon,
    title: 'My Photos',
    visible: true,
    showInDrawer: true,
    mobileSlot: 'more',
  },
  {
    name: 'hub/my-pets',
    path: '/hub/my-pets',
    Icon: PetsIcon,
    title: 'My Pets',
    visible: true,
    showInDrawer: true,
    mobileSlot: 'more',
  },
  {
    name: 'hub/settings',
    path: '/hub/settings',
    Icon: SettingsIcon,
    title: 'Settings',
    visible: true,
    showInDrawer: true,
    mobileSlot: 'more',
  },
];

// Pre-surrender parents get the reduced five-item set (ticket 2530 + QA round).
// All five fit the mobile bar, so none go to the More sheet (the bar renders
// More only when a sheet item exists — see HubBottomNav). Fosters get seven items
// and keep each item's own mobile slot.
export const getNavItems = (membershipExpired = false, audience: HubAudience = 'adopter'): NavItem[] => {
  const items = getAllNavItems(membershipExpired);
  if (audience === 'foster') return items.filter((item) => FOSTER_NAV.has(item.name));
  if (audience === 'preSurrender') {
    return items
      .filter((item) => PRE_SURRENDER_NAV.has(item.name))
      .map((item) => ({ ...item, mobileSlot: 'bar' as MobileSlot }));
  }
  return items;
};
